import { isObject } from 'lodash'
import { Properties } from '../entities'
import { getIdOrNull, isNonEmptyArray } from '../index'
import { ValidationFields } from '../validators'

export const Formats = Object.freeze({
  DEFAULT: 'default',
  PUBLIC_CREATE: 'publicCreate'
})

const publicCreateKeys = new Set([
  Properties.ABBREVIATION,
  Properties.ACTS_MANDATORY,
  Properties.COMMENT,
  Properties.NAME,
  Properties.SCHOOL,
  Properties.SECTION_TO_YEAR
])

/** @enum {string} */
export const SchoolSectionProperties = Object.freeze({
  SCHOOL: ValidationFields.SCHOOL,
  SCHOOL_YEAR: ValidationFields.SCHOOL_YEAR,
  SECTION: ValidationFields.SECTION,
  COMMENT: ValidationFields.COMMENT
})

export const SCHOOL_SECTION_VALIDATORS = [
  { key: SchoolSectionProperties.SCHOOL, validator: ss => !!getIdOrNull(ss.school) }
]

export const PortalSectionsStepValidators = Object.freeze({
  [Properties.SECTIONS]: sections => isNonEmptyArray(sections)
})

export const getFullAbbreviation = ss => {
  let abbreviation = ''

  if (ss.abbreviation) {
    abbreviation += `${ss.abbreviation}`

    if (ss.schoolYear) {
      abbreviation += ` (${ss.schoolYear?.name})`
    } else if (ss.schoolYearName) {
      abbreviation += ` (${ss.schoolYearName})`
    }
  } else if (ss.sectionToYear) {
    abbreviation += `${ss.sectionToYear.sectionAbbreviation} (${ss.sectionToYear.schoolYearName})`
  }

  return abbreviation
}

export const getComment = ss => {
  return ss.comment ?? null
}

export const sortByAbbreviation = (a, b) => {
  if (!isObject(a)) {
    return 1
  }
  if (!isObject(b)) {
    return -1
  }

  return a.abbreviation.localeCompare(b.abbreviation)
}

export const reduceBySectionsAndYears = schoolSections => {
  return schoolSections.reduce((acc, ss) => {
    const key = ss.sectionToYear.section.id

    if (!acc.sectionToYearsBySection[key]) {
      acc.sections.push({ ...ss.sectionToYear.section })
      acc.sectionToYearsBySection[key] = []
    }

    acc.sectionToYearsBySection[key].push(ss.sectionToYear)

    return acc
  }, { sections: [], sectionToYearsBySection: {} })
}

export const toCreateSchoolFormat = object => ({
  abbreviation: object.abbreviation,
  actsMandatory: object.actsMandatory,
  comment: object.comment,
  name: object.name,
  school: object.school,
  sectionToYear: object.sectionToYear
})

export class SchoolSection {
  constructor ({ id, abbreviation, actsMandatory, comment, dumpSection, name, school, schoolYear, section, sectionToYear } = {}) {
    this.id = id ?? -1
    this.abbreviation = abbreviation ?? ''
    this.actsMandatory = actsMandatory ?? false
    this.comment = comment ?? null
    this.dumpSection = dumpSection ?? false
    this.name = name ?? ''
    this.school = school ?? null
    this.schoolYear = schoolYear ?? { id: -1 }
    this.section = section ?? null
    this.sectionToYear = sectionToYear ?? null
  }

  toRequestBody = (format = Formats.DEFAULT) => {
    const body = {
      id: this.id,
      abbreviation: this.abbreviation,
      actsMandatory: this.actsMandatory,
      comment: this.comment,
      dumpSection: this.dumpSection,
      name: this.name,
      school: getIdOrNull(this.school),
      schoolYearId: getIdOrNull(this.schoolYear),
      schoolYearName: this.schoolYear?.name,
      sectionId: getIdOrNull(this.section),
      sectionToYear: getIdOrNull(this.sectionToYear)
    }

    if (format === Formats.PUBLIC_CREATE) {
      Object.keys(body).forEach(key => {
        if (!publicCreateKeys.has(key)) {
          delete body[key]
        }
      })
    }

    return body
  }
}
